<template>
  <div class="d-flex flex-column">
    <div
      v-if="['idle', 'progress'].includes(removeObserverStatus)"
      class="pa-0 text-black mb-4"
    >
      <I18nT keypath="dialogs.document_dialog.subtitles.remove_observer" />
    </div>
    <v-alert
      v-if="removeObserverStatus === 'error'"
      class="mb-6 text-body-small"
      data-test-id="deep-sign-alert"
      type="error"
      variant="tonal"
    >
      <div class="text-body-medium">
        {{ i18n.t('errors.generic') }}
      </div>
    </v-alert>

    <portal to="document-dialog-actions">
      <VBtnSecondary variant="outlined" @click="emit('closeDialog')">
        <I18nT
          :keypath="
            removeObserverStatus === 'error'
              ? 'buttons.close'
              : 'buttons.cancel'
          "
        />
      </VBtnSecondary>
      <VBtnPrimary
        v-if="['idle', 'progress'].includes(removeObserverStatus)"
        ref="removeObserverButton"
        color="primary"
        data-test-id="remove-observer"
        @click="removeObserver"
      >
        <I18nT keypath="buttons.remove_observer" />
      </VBtnPrimary>
    </portal>
  </div>
</template>

<script setup lang="ts">
import I18nT from '@/components/I18nT.vue';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useCreateStore } from '@/stores/create';
import { useGlobalStore } from '@/stores/global.ts';
import { useMessageStore } from '@/stores/message.ts';
import { useSignStore } from '@/stores/sign.ts';
import type { ComponentState } from '@/types/ui';
import { nextTick } from 'vue';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useApi } from '@/composables/useApi.ts';

const { deepSignAPI } = useApi();
const emit = defineEmits(['closeDialog']);
const removeObserverStatus = ref<ComponentState>('idle');
const route = useRoute();
const { currentDocument } = useDocumentContext();
const globalStore = useGlobalStore();
const createStore = useCreateStore();
const signStore = useSignStore();
const messageStore = useMessageStore();
const i18n = useI18n();
const removeObserverButton = ref();

async function removeObserver() {
  try {
    removeObserverStatus.value = 'progress';
    if (globalStore.selectedObserver && currentDocument.value) {
      if (
        route.name === 'p-document-create' ||
        route.name === 'p-document-view'
      ) {
        await createStore.deleteObserver(
          currentDocument.value.documentId,
          globalStore.selectedObserver.observerId
        );
      } else {
        await deepSignAPI.value.documentsObservers.deleteById(
          currentDocument.value.documentId,
          globalStore.selectedObserver.observerId
        );
        signStore.removeObserver(globalStore.selectedObserver.observerId);
      }
      removeObserverStatus.value = 'success';
      messageStore.showMessage({
        text: i18n.t('snackbars.observer_removed'),
        color: 'success',
        icon: 'fa fa-circle-check'
      });
      emit('closeDialog');
    } else {
      removeObserverStatus.value = 'idle';
    }
  } catch (error) {
    removeObserverStatus.value = 'error';
  }
}

onMounted(() => {
  nextTick(() => {
    removeObserverButton.value.$el.focus();
  });
});
</script>
